/**
 * Admin page to manage configurable AI command prompts.
 *
 * Where: `/admin/commands` route in the dashboard shell.
 * Uses: `commandApi`, React Query, and MUI table/dialog components.
 * Behavior: shows effective command values (default + override state), supports edit/save and reset-to-default.
 * Invariants: only admin/staff should mutate; backend enforces access control.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { commandApi } from "@/api/command";
import { useAuth } from "@/hooks/useAuth";
import type { AiCommand, AiCommandList } from "@/types/command";
import {
  CommandsTable,
  EditCommandDialog,
  ResetCommandDialog,
} from "@/components/commands";

const PAGE_SIZE = 25;
type CategoryFilter = "all" | string;

export const Commands: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>("all");
  const [editTarget, setEditTarget] = useState<AiCommand | null>(null);
  const [resetTarget, setResetTarget] = useState<AiCommand | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    data: commandsPages,
    isLoading: isLoadingCommands,
    error: commandsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["ai-commands", search, categoryFilter],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      commandApi.list({
        search: search || undefined,
        category: categoryFilter !== "all" ? categoryFilter : undefined,
        page_token: pageParam,
        max_results: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage: AiCommandList) =>
      lastPage.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: isAdmin,
  });

  const { data: categoryOptions = [] } = useQuery({
    queryKey: ["ai-command-categories"],
    queryFn: () => commandApi.listCategories(),
    enabled: isAdmin,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const invalidateCommands = () => {
    queryClient.invalidateQueries({ queryKey: ["ai-commands"] });
    queryClient.invalidateQueries({ queryKey: ["ai-command-categories"] });
  };

  const updateMutation = useMutation({
    mutationFn: (payload: { name: string; command: string; description?: string; category?: string }) =>
      commandApi.update(payload.name, {
        command: payload.command,
        description: payload.description,
        category: payload.category,
      }),
    onSuccess: () => {
      setEditTarget(null);
      invalidateCommands();
    },
  });

  const resetMutation = useMutation({
    mutationFn: (name: string) => commandApi.reset(name),
    onSuccess: () => {
      setResetTarget(null);
      invalidateCommands();
    },
  });

  const allRows = useMemo(
    () =>
      commandsPages?.pages.flatMap((page: AiCommandList) => page.items) ?? [],
    [commandsPages],
  );

  const rows = useMemo(() => allRows, [allRows]);

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Access denied: admin/staff only.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight={700} mb={1}>
          AI Commands
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage editable AI instruction prompts. Reset removes DB override and falls back to
          default registry.
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search command name, category, or description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="commands-category-filter-label">Category</InputLabel>
            <Select
              labelId="commands-category-filter-label"
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categoryOptions.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {commandsError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load commands.
        </Alert>
      ) : null}

      <CommandsTable
        rows={rows}
        isLoading={isLoadingCommands}
        onEdit={(row) => setEditTarget(row)}
        onReset={(row) => setResetTarget(row)}
      />

      <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Loaded: {rows.length}
        </Typography>
        {isFetchingNextPage ? <CircularProgress size={20} /> : null}
      </Box>

      <div ref={loadMoreRef} style={{ height: 20 }} />

      <EditCommandDialog
        open={!!editTarget}
        target={editTarget}
        isPending={updateMutation.isPending}
        isError={updateMutation.isError}
        onClose={() => setEditTarget(null)}
        onSave={(payload) => updateMutation.mutate(payload)}
      />

      <ResetCommandDialog
        open={!!resetTarget}
        target={resetTarget}
        isPending={resetMutation.isPending}
        isError={resetMutation.isError}
        onClose={() => setResetTarget(null)}
        onConfirm={(name) => resetMutation.mutate(name)}
      />
    </Box>
  );
};

export default Commands;

